import { RichTextDisplay } from "@/components/RichTextEditor";

/**
 * Monster-Manual style creature stat block.
 *
 * Parchment scroll background, dark-red serif name, red horizontal dividers,
 * two-column body for AC/HP/Speed + abilities + Senses/Languages/CR on the left
 * and Actions / Bonus Actions / Reactions / Legendary Actions on the right.
 *
 * Replaces the dark-theme variant — used on the Creatures page (slide panel + grid).
 */

const abilityMod = (score) => {
  const s = parseInt(score, 10);
  if (Number.isNaN(s)) return "+0";
  const mod = Math.floor((s - 10) / 2);
  return (mod >= 0 ? "+" : "") + mod;
};

const PALETTE = {
  bg: "#f4ead0",
  bgAlt: "#ebdfb8",
  border: "#c9b27a",
  red: "#7b1f1f",
  redAccent: "#c43232",
  text: "#3d2519",
  muted: "#7a5a35",
};

const RedDivider = () => (
  <div className="my-2">
    <div style={{ height: 2, background: PALETTE.redAccent }} />
    <div style={{ height: 1, background: PALETTE.redAccent, opacity: 0.5, marginTop: 1 }} />
  </div>
);

const SectionHeader = ({ children }) => (
  <div className="mt-4 mb-1">
    <h3 className="font-serif text-xl" style={{ color: PALETTE.red }}>{children}</h3>
    <div style={{ height: 1, background: PALETTE.redAccent, opacity: 0.6 }} />
  </div>
);

const Fact = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <p className="text-sm leading-snug" data-testid={`statblock-fact-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="font-bold" style={{ color: PALETTE.text }}>{label}</span>{" "}
      <span style={{ color: PALETTE.text }}>{value}</span>
    </p>
  );
};

const ActionList = ({ items, label }) => {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={`statblock-section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <SectionHeader>{label}</SectionHeader>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <p key={idx} className="text-sm leading-relaxed" style={{ color: PALETTE.text }}>
            <strong className="italic">{it.name}.</strong> {it.description}
          </p>
        ))}
      </div>
    </div>
  );
};

export const CreatureStatBlock = ({ creature, getImageUrl }) => {
  if (!creature) return null;
  const c = creature;
  const headerLine = [c.size, c.creature_type, c.alignment].filter(Boolean).join(", ");

  const abilities = [
    ["STR", c.str_score],
    ["DEX", c.dex_score],
    ["CON", c.con_score],
    ["INT", c.int_score],
    ["WIS", c.wis_score],
    ["CHA", c.cha_score],
  ];
  const hasAbilities = abilities.some(([, v]) => v !== undefined && v !== null && v !== "");

  return (
    <div
      data-testid="creature-stat-block"
      className="rounded-md max-w-4xl overflow-hidden"
      style={{
        background: PALETTE.bg,
        border: `1px solid ${PALETTE.border}`,
        color: PALETTE.text,
        backgroundImage:
          "radial-gradient(circle at 0% 0%, rgba(255,255,255,0.5), transparent 60%), " +
          "radial-gradient(circle at 100% 100%, rgba(0,0,0,0.06), transparent 65%)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
      }}
    >
      {/* Top decorative red band */}
      <div style={{ height: 4, background: PALETTE.redAccent }} />

      <div className="p-5 md:p-6">
        {/* Portrait (if any) */}
        {getImageUrl && getImageUrl(c) && (
          <div className="float-right ml-4 mb-2 w-32 h-32 rounded-sm overflow-hidden" style={{ border: `2px solid ${PALETTE.border}` }} data-testid="statblock-portrait">
            <img src={getImageUrl(c)} alt={c.name} className="w-full h-full object-cover" crossOrigin="use-credentials" />
          </div>
        )}

        {/* Name + type line */}
        <h2 className="font-serif text-3xl md:text-4xl tracking-wide leading-none" style={{ color: PALETTE.red }} data-testid="statblock-name">
          {c.name}
        </h2>
        {headerLine && (
          <p className="text-sm md:text-base italic mt-1" style={{ color: PALETTE.muted }} data-testid="statblock-type-line">{headerLine}</p>
        )}

        <RedDivider />

        {/* Two-column body */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {/* LEFT COLUMN */}
          <div className="space-y-1">
            <Fact label="Armor Class" value={c.armor_class} />
            <Fact label="Hit Points" value={c.hit_points} />
            <Fact label="Speed" value={c.speed} />

            {hasAbilities && (
              <>
                <RedDivider />
                <div className="grid grid-cols-6 gap-2 text-center my-1" data-testid="statblock-abilities">
                  {abilities.map(([label]) => (
                    <div key={label}>
                      <p className="text-[11px] font-bold tracking-wider" style={{ color: PALETTE.text }}>{label}</p>
                    </div>
                  ))}
                  {abilities.map(([label, score]) => {
                    const v = score || 10;
                    return (
                      <div key={`v-${label}`} className="text-sm tabular-nums">{v} ({abilityMod(v)})</div>
                    );
                  })}
                </div>
              </>
            )}

            <RedDivider />

            <Fact label="Saving Throws" value={c.saving_throws} />
            <Fact label="Skills" value={c.skills} />
            <Fact label="Damage Resistances" value={c.damage_resistances} />
            <Fact label="Damage Immunities" value={c.damage_immunities} />
            <Fact label="Condition Immunities" value={c.condition_immunities} />
            <Fact label="Senses" value={c.senses} />
            <Fact label="Languages" value={c.languages} />
            <Fact label="Challenge" value={c.challenge_rating ? `${c.challenge_rating}${c.xp ? ` (${c.xp} XP)` : ""}` : ""} />
            {c.proficiency_bonus && <Fact label="Proficiency Bonus" value={c.proficiency_bonus} />}

            {c.traits?.length > 0 && (
              <>
                <SectionHeader>Traits</SectionHeader>
                <div className="space-y-2">
                  {c.traits.map((t, idx) => (
                    <p key={idx} className="text-sm leading-relaxed" style={{ color: PALETTE.text }}>
                      <strong className="italic">{t.name}.</strong> {t.description}
                    </p>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* RIGHT COLUMN */}
          <div>
            <ActionList items={c.actions} label="Actions" />
            <ActionList items={c.bonus_actions} label="Bonus Actions" />
            <ActionList items={c.reactions} label="Reactions" />
            {c.legendary_actions?.length > 0 && (
              <div data-testid="statblock-section-legendary-actions">
                <SectionHeader>Legendary Actions</SectionHeader>
                {c.legendary_description && (
                  <p className="text-sm italic mb-2 leading-relaxed" style={{ color: PALETTE.muted }}>{c.legendary_description}</p>
                )}
                <div className="space-y-2">
                  {c.legendary_actions.map((la, idx) => (
                    <p key={idx} className="text-sm leading-relaxed" style={{ color: PALETTE.text }}>
                      <strong className="italic">{la.name}.</strong> {la.description}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description / Lore */}
        {c.description && (
          <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${PALETTE.border}` }} data-testid="statblock-description">
            <h3 className="font-bold text-base mb-2" style={{ color: PALETTE.text }}>Description</h3>
            <div className="text-sm leading-relaxed" style={{ color: PALETTE.text }}>
              <RichTextDisplay content={c.description} />
            </div>
          </div>
        )}

        <div style={{ clear: "both" }} />
      </div>

      <div style={{ height: 4, background: PALETTE.redAccent }} />
    </div>
  );
};

export default CreatureStatBlock;
